import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { BalanceRounded as TrialBalanceIcon } from "@mui/icons-material";
import axios from "axios";
import { AutoTaskAuthContext } from "../../../Context/AutoTaskContext";
import { usePawnLedgerScope } from "../pawnLedgerScope";
import { reportPageSx, reportTableSx } from "../../PawnShop/reportLayout";
import ReportDateWindow from "../../PawnShop/reportDateWindow";
import { DEFAULT_SORT, sortRows } from "../../PawnShop/reportSort";
import {
  BLUE,
  BLUE_DARK,
  BORDER,
  DANGER,
  EmptyState,
  FilterActions,
  FilterCard,
  HEAD_BG,
  INK,
  INK_MUTED,
  INK_SOFT,
  LoadingPanel,
  ResultsHeader,
  SUCCESS,
  TODAY,
  cellSx,
  fmtAmt,
  fmtAmtAlways,
  fmtBalance,
  footCellSx,
  monthStart,
  headCellSx,
  pdfTableTheme,
  rangeLabel,
  resultsCardSx,
  startPdf,
} from "./ledgerReportKit";

const API = process.env.REACT_APP_API_BASE_URL;

// The four top-level books, plus "All". A numeric option is a real GROUP row
// from account_master; these five are categories the API matches by name.
const CATEGORY_OPTIONS = [
  { account_id: "ALL", account_name: "All", isStatic: true },
  { account_id: "ASSET", account_name: "Asset", isStatic: true },
  { account_id: "LIABILITY", account_name: "Liability", isStatic: true },
  { account_id: "INCOME", account_name: "Income", isStatic: true },
  { account_id: "EXPENSE", account_name: "Expense", isStatic: true },
];

// Rows are ACCOUNTS, not transactions, so the register sorts on what an account
// has. The sort runs INSIDE each group — reordering across groups would break
// the book the accounts are filed under.
const SORT_OPTIONS = [
  { key: "name", label: "Account Name" },
  { key: "debit", label: "Debit" },
  { key: "credit", label: "Credit" },
  { key: "closing", label: "Closing Balance" },
];

/** Signed value of a balance, so Dr and Cr can be compared as one number. */
const signed = (value, type) =>
  (parseFloat(value) || 0) * (type === "CREDIT" ? -1 : 1);

/**
 * The trial balance for one book (or all of them) — every account's opening
 * balance, its movement over the window, and what it closes at.
 *
 * "Single Day" ticked reads the balance AS ON that day (the API's `as_on`
 * mode); unticking it slides a To field open and the report becomes the
 * movement BETWEEN the two days (`between`).
 */
const PawnTrialBalance = ({ embedded = false }) => {
  const { userInfo, currentStoreInfo, defaultFY, defaultFYId } =
    useContext(AutoTaskAuthContext) || {};
  const storeId = userInfo?.store_id;
  const { pawnMode, scope: pawnScope } = usePawnLedgerScope();

  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState(CATEGORY_OPTIONS[0]);

  // A trial balance is normally read as a position on a day, so it opens in
  // as-on mode — unlike the ledger and cash book, which open on a period.
  const [dateMode, setDateMode] = useState("single");
  const [singleDate, setSingleDate] = useState(TODAY());
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(TODAY());

  const [showZero, setShowZero] = useState(false);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const showError = useCallback(
    (message) => setSnackbar({ open: true, message }),
    [],
  );

  const asOn = dateMode === "single";
  const effFrom = asOn ? singleDate : fromDate;
  const effTo = asOn ? singleDate : toDate;

  // ── Groups for the picker ─────────────────────────────────────────────────
  useEffect(() => {
    if (storeId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/accounts/accounts/${storeId}/groups-only`,
        );
        if (!cancelled && Array.isArray(data?.data)) setGroups(data.data);
      } catch {
        /* The five categories alone are a usable picker; don't nag. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const groupOptions = useMemo(
    () => [
      ...CATEGORY_OPTIONS,
      ...groups.map((g) => ({
        account_id: g.account_id,
        account_name: g.account_name,
      })),
    ],
    [groups],
  );

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const buildReport = useCallback(async () => {
    if (storeId == null || !defaultFYId || !group) return;
    setLoading(true);
    try {
      const payload = {
        store_id: parseInt(storeId, 10),
        filter_mode: asOn ? "as_on" : "between",
        ...(asOn
          ? { as_on_date: singleDate }
          : { from_date: fromDate, to_date: toDate }),
        ...pawnScope,
      };
      const all = group.account_id === "ALL";
      const url = all
        ? `${API}/api/accounts/trial-balance/all-groups/${defaultFYId}`
        : `${API}/api/accounts/trial-balance/by-group/${defaultFYId}`;
      const { data } = await axios.post(
        url,
        all ? payload : { ...payload, group_id: group.account_id },
      );
      if (data?.success) setReport({ ...data.data, all });
      else {
        setReport(null);
        showError(data?.message || "Failed to generate the trial balance.");
      }
    } catch (err) {
      setReport(null);
      // "No accounts found for given group" is a 404 and a perfectly ordinary
      // answer for an empty group — show the empty state, not an error toast.
      const message = err?.response?.data?.message || "";
      setReport(null);
      if (!/no accounts found/i.test(message))
        showError(message || "Failed to generate the trial balance.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeId,
    defaultFYId,
    group,
    asOn,
    singleDate,
    fromDate,
    toDate,
    pawnMode,
    showError,
  ]);

  useEffect(() => {
    buildReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, pawnMode, group, dateMode, singleDate, fromDate, toDate]);

  const handleReset = () => {
    setGroup(CATEGORY_OPTIONS[0]);
    setDateMode("single");
    setSingleDate(TODAY());
    setFromDate(monthStart());
    setToDate(TODAY());
    setShowZero(false);
    setSort(DEFAULT_SORT);
  };

  // ── Rows ──────────────────────────────────────────────────────────────────
  // One flat row list drives both the table and the PDF, so they can never
  // drift: a `group` banner followed by its `account` rows, indented.
  const figures = useCallback(
    (node) => ({
      opening: asOn
        ? parseFloat(node.opening_balance) || 0
        : parseFloat(node.period_opening_balance) || 0,
      openingType: asOn
        ? node.opening_balance_type
        : node.period_opening_balance_type,
      debit: asOn
        ? parseFloat(node.total_debit) || 0
        : parseFloat(node.period_debit) || 0,
      credit: asOn
        ? parseFloat(node.total_credit) || 0
        : parseFloat(node.period_credit) || 0,
      closing: parseFloat(node.closing_balance) || 0,
      closingType: node.balance_type,
    }),
    [asOn],
  );

  const rows = useMemo(() => {
    if (!report) return [];

    // An account with no opening, no movement and no closing is noise unless
    // the reader asked to see the full chart of accounts.
    const isEmpty = (f) => !f.opening && !f.debit && !f.credit && !f.closing;

    const accountRows = (list) =>
      sortRows(
        list
          .map((a) => ({ kind: "account", label: a.account_name, ...figures(a) }))
          .filter((r) => showZero || !isEmpty(r)),
        sort,
        {
          name: (r) => r.label,
          debit: (r) => r.debit,
          credit: (r) => r.credit,
          closing: (r) => signed(r.closing, r.closingType),
        },
      );

    if (!report.all)
      return [
        {
          kind: "group",
          label: report.group_name || group?.account_name || "Group",
        },
        ...accountRows(report.trial_balance || []),
      ];

    return (report.categories || []).flatMap((cat) => {
      const kids = accountRows(cat.children || []);
      // A category with nothing under it prints only when the reader asked for
      // the whole chart — otherwise it is a heading over a blank space.
      if (!kids.length && !showZero) return [];
      return [
        { kind: "group", label: cat.account_name, ...figures(cat) },
        ...kids,
      ];
    });
  }, [report, figures, showZero, sort, group]);

  // Totalled from the ACCOUNT rows on screen rather than from the payload's
  // own `total_debit` / `total_credit`: those sum the CATEGORY closing
  // balances by side, which nets Income against Expense and prints a figure
  // (5,000.00 on a book whose accounts moved 12.5 lakh) that has no relation
  // to the columns above it. Summing what is displayed is the number a trial
  // balance is actually read for, and it can never disagree with the rows.
  const totals = useMemo(() => {
    const accounts = rows.filter((r) => r.kind === "account");
    const debit = accounts.reduce((sum, r) => sum + r.debit, 0);
    const credit = accounts.reduce((sum, r) => sum + r.credit, 0);
    // Dr positive, Cr negative — a whole book that balances nets to zero. A
    // single GROUP is not expected to: it is one side of the entries.
    const closingNet = accounts.reduce(
      (sum, r) => sum + signed(r.closing, r.closingType),
      0,
    );
    return {
      count: accounts.length,
      debit,
      credit,
      closingNet,
      balanced: Math.abs(closingNet) < 0.01,
    };
  }, [rows]);

  const accountCount = totals.count;

  // ── PDF ───────────────────────────────────────────────────────────────────
  const generatePDF = () => {
    if (!report) return;
    const { doc, margin, startY } = startPdf({
      store: currentStoreInfo,
      title: `Trial Balance — ${group?.account_name || "All"}`,
      context: [
        asOn ? `As on ${rangeLabel(effFrom, effTo)}` : rangeLabel(effFrom, effTo),
        defaultFY ? `FY ${defaultFY}-${defaultFY + 1}` : null,
      ]
        .filter(Boolean)
        .join("  |  "),
    });

    doc.autoTable({
      startY,
      head: [["Particulars", "Opening", "Debit", "Credit", "Closing"]],
      body: rows.map((r) => {
        const cells = [
          { content: `${r.kind === "account" ? "    " : ""}${r.label}` },
          {
            content: r.kind === "group" && r.openingType === undefined
              ? ""
              : fmtBalance(r.opening, r.openingType),
            styles: { halign: "right" },
          },
          { content: fmtAmt(r.debit), styles: { halign: "right" } },
          { content: fmtAmt(r.credit), styles: { halign: "right" } },
          {
            content: r.kind === "group" && r.closingType === undefined
              ? ""
              : fmtBalance(r.closing, r.closingType),
            styles: { halign: "right" },
          },
        ];
        if (r.kind === "group")
          cells.forEach((c) => {
            c.styles = { ...c.styles, fontStyle: "bold" };
          });
        return cells;
      }),
      foot: [
        [
          { content: "Total", styles: { halign: "left" } },
          { content: "", styles: {} },
          { content: fmtAmtAlways(totals.debit), styles: { halign: "right" } },
          { content: fmtAmtAlways(totals.credit), styles: { halign: "right" } },
          {
            content: report.all
              ? totals.balanced
                ? "Balanced"
                : `Diff ${fmtAmtAlways(Math.abs(totals.closingNet))}`
              : fmtBalance(
                  Math.abs(totals.closingNet),
                  totals.closingNet < 0 ? "CREDIT" : "DEBIT",
                ),
            styles: { halign: "right" },
          },
        ],
      ],
      footStyles: {
        fillColor: [248, 250, 252],
        textColor: [15, 23, 42],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 38 },
        2: { halign: "right", cellWidth: 32 },
        3: { halign: "right", cellWidth: 32 },
        4: { halign: "right", cellWidth: 38 },
      },
      ...pdfTableTheme(margin),
    });

    doc.save(`Trial_Balance_${effFrom}_to_${effTo}.pdf`);
  };

  return (
    <Box sx={reportPageSx(embedded)}>
      <FilterCard>
        <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={groupOptions}
              value={group}
              onChange={(_e, v) => setGroup(v || CATEGORY_OPTIONS[0])}
              getOptionLabel={(o) => o?.account_name || ""}
              isOptionEqualToValue={(o, v) =>
                String(o.account_id) === String(v?.account_id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Group"
                  placeholder="All"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <ReportDateWindow
            label="Period"
            dateMode={dateMode}
            setDateMode={setDateMode}
            singleDate={singleDate}
            setSingleDate={setSingleDate}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
          />

          <Grid item xs={12} md="auto">
            <FilterActions
              onView={buildReport}
              onReset={handleReset}
              loading={loading}
              sortOptions={SORT_OPTIONS}
              sort={sort}
              onSortChange={setSort}
            />
          </Grid>
        </Grid>

        {/* ── Report options ── */}
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px dashed ${BORDER}`,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 1.5,
            rowGap: 0.25,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showZero}
                onChange={() => setShowZero((v) => !v)}
                sx={{ py: 0.25, color: INK_MUTED, "&.Mui-checked": { color: BLUE } }}
              />
            }
            label="Show Nil Balances"
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": {
                fontSize: "0.78rem",
                fontWeight: 600,
                color: INK_SOFT,
              },
            }}
          />
          {/* Only the WHOLE chart is expected to balance — one group is one
              side of the entries, so the badge would cry wolf on every group. */}
          {report?.all && rows.length > 0 && (
            <Chip
              size="small"
              label={
                totals.balanced
                  ? "Balanced"
                  : `Out by ${fmtAmtAlways(Math.abs(totals.closingNet))}`
              }
              sx={{
                fontWeight: 700,
                fontSize: "0.72rem",
                bgcolor: totals.balanced ? "#ECFDF5" : "#FEF2F2",
                color: totals.balanced ? SUCCESS : DANGER,
              }}
            />
          )}
        </Box>
      </FilterCard>

      <Card elevation={0} sx={resultsCardSx}>
        <ResultsHeader
          title={`TRIAL BALANCE ${
            asOn ? `AS ON ${rangeLabel(effFrom, effTo)}` : rangeLabel(effFrom, effTo)
          }`}
          subtitle={[
            group?.account_name || "All",
            defaultFY ? `FY ${defaultFY}-${defaultFY + 1}` : null,
          ]
            .filter(Boolean)
            .join("  ·  ")}
          count={
            report
              ? `${accountCount} ${accountCount === 1 ? "account" : "accounts"}`
              : null
          }
          onPdf={generatePDF}
          pdfDisabled={loading || !rows.length}
        />

        {loading ? (
          <LoadingPanel />
        ) : !report || rows.length === 0 ? (
          <EmptyState icon={<TrialBalanceIcon />}>
            {report
              ? `Nothing on ${group?.account_name || "these accounts"} for ${rangeLabel(
                  effFrom,
                  effTo,
                )}. Tick "Show Nil Balances" to list every account.`
              : "Pick a group and a date to read the trial balance."}
          </EmptyState>
        ) : (
          <TableContainer sx={reportTableSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Particulars</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 150 }}>
                    Opening
                  </TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 130 }}>
                    Debit
                  </TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 130 }}>
                    Credit
                  </TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 150 }}>
                    Closing
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r, i) =>
                  r.kind === "group" ? (
                    <TableRow key={`g-${i}`}>
                      <TableCell
                        sx={{
                          ...cellSx,
                          fontWeight: 800,
                          color: INK,
                          bgcolor: HEAD_BG,
                        }}
                      >
                        {r.label}
                      </TableCell>
                      <TableCell align="right" sx={{ ...cellSx, bgcolor: HEAD_BG }}>
                        {r.openingType ? fmtBalance(r.opening, r.openingType) : ""}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...cellSx, bgcolor: HEAD_BG, fontWeight: 700 }}
                      >
                        {fmtAmt(r.debit)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...cellSx, bgcolor: HEAD_BG, fontWeight: 700 }}
                      >
                        {fmtAmt(r.credit)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...cellSx,
                          bgcolor: HEAD_BG,
                          fontWeight: 800,
                          color: BLUE_DARK,
                        }}
                      >
                        {r.closingType ? fmtBalance(r.closing, r.closingType) : ""}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={`a-${i}`} hover>
                      <TableCell sx={{ ...cellSx, pl: 4 }}>{r.label}</TableCell>
                      <TableCell align="right" sx={{ ...cellSx, color: INK_MUTED }}>
                        {fmtBalance(r.opening, r.openingType)}
                      </TableCell>
                      <TableCell align="right" sx={cellSx}>
                        {fmtAmt(r.debit)}
                      </TableCell>
                      <TableCell align="right" sx={cellSx}>
                        {fmtAmt(r.credit)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...cellSx, color: INK, fontWeight: 600 }}
                      >
                        {fmtBalance(r.closing, r.closingType)}
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TableCell sx={footCellSx}>Total</TableCell>
                  <TableCell sx={footCellSx} />
                  <TableCell align="right" sx={{ ...footCellSx, color: SUCCESS }}>
                    {fmtAmtAlways(totals.debit)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...footCellSx, color: SUCCESS }}>
                    {fmtAmtAlways(totals.credit)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      ...footCellSx,
                      color: report.all && !totals.balanced ? DANGER : INK,
                    }}
                  >
                    {report.all
                      ? totals.balanced
                        ? "Balanced"
                        : `Diff ${fmtAmtAlways(Math.abs(totals.closingNet))}`
                      : fmtBalance(
                          Math.abs(totals.closingNet),
                          totals.closingNet < 0 ? "CREDIT" : "DEBIT",
                        )}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnTrialBalance;
